<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';

export default {
  name: 'CreateContactGroupModal',
  props: {
    selectedContacts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'success'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: '',
      loading: false,
    };
  },
  computed: {
    contactCount() {
      return this.selectedContacts.length;
    },
    contactPreview() {
      return this.selectedContacts.slice(0, 5);
    },
    hasMoreContacts() {
      return this.contactCount > this.contactPreview.length;
    },
  },
  validations() {
    return {
      name: { required },
    };
  },
  methods: {
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.loading = true;
      try {
        const payload = {
          campaign_contact_group: {
            name: this.name.trim(),
            contact_ids: this.selectedContacts.map(contact => contact.id),
          },
        };
        const { data } = await CampaignContactGroupsAPI.create(payload);
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUP.SUCCESS'));
        this.$emit('success', data.group);
        this.$emit('close');
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUP.ERROR');
        useAlert(message);
      } finally {
        this.loading = false;
      }
    },
    onCancel() {
      this.$emit('close');
    },
    contactDisplayValue(contact) {
      return (
        contact.name ||
        contact.phone_number ||
        contact.email ||
        contact.identifier ||
        contact.id
      );
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ADD.CONTACTS.GROUP.TITLE')"
      :header-content="$t('CAMPAIGN.ADD.CONTACTS.GROUP.DESCRIPTION')"
    />

    <div class="flex flex-col gap-2">
      <label class="woot-form-label" for="contact-group-name">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_LABEL') }}
      </label>
      <input
        id="contact-group-name"
        v-model.trim="name"
        type="text"
        class="woot-input"
        :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_PLACEHOLDER')"
        :class="{ error: v$.name.$error }"
        @blur="v$.name.$touch"
      />
      <span v-if="v$.name.$error" class="message text-sm">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.NAME_ERROR') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CONTACT_COUNT', { count: contactCount }) }}
      </p>
      <div class="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3 max-h-32 overflow-y-auto">
        <ul class="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
          <li 
            v-for="contact in contactPreview" 
            :key="`group-preview-${contact.id}`"
            class="truncate"
          >
            • {{ contactDisplayValue(contact) }}
          </li>
        </ul>
        <p v-if="hasMoreContacts" class="text-xs text-slate-500 dark:text-slate-400 mt-2">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.MORE_CONTACTS', { remaining: contactCount - contactPreview.length }) }}
        </p>
      </div>
    </div>

    <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-3">
      <p class="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.HELP') }}
      </p>
    </div>

    <div class="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
      <woot-button variant="clear" type="button" @click="onCancel">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.CANCEL') }}
      </woot-button>
      <woot-button
        variant="primary"
        type="button"
        :loading="loading"
        :disabled="!contactCount || loading"
        @click="onSubmit"
      >
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP.SUBMIT') }}
      </woot-button>
    </div>
  </div>
</template>

<style scoped>
.message {
  color: var(--r-500);
  display: block;
  margin-top: 0.25rem;
}

.woot-input.error {
  border-color: var(--r-500);
}

.woot-input.error:focus {
  border-color: var(--r-500);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}
</style>
